import { useState } from "react";
import { Link } from "react-router-dom";
import PageLayout from "@/components/layout/PageLayout";
import PageHero from "@/components/shared/PageHero";
import CityAutocomplete from "@/components/base/CityAutocomplete";
import { INDIAN_STATES } from "@/constants/indianStates";
import { useFormSubmit } from "@/hooks/useFormSubmit";
import { usePageContent } from "@/hooks/usePageContent";
import { useSettings } from "@/context/SettingsContext";
import { usePageMeta } from "@/hooks/usePageMeta";

function stripHtml(html: string): string {
  const div = document.createElement('div');
  div.innerHTML = html;
  return div.textContent || div.innerText || '';
}

const reportIncludes = [
  "Full HBU (Highest & Best Use) scoring breakdown",
  "Top 3 monetization strategies ranked by ROI",
  "5-year financial projections & appreciation forecast",
  "Zoning & FSI compliance analysis",
  "Brand/developer shortlist matched to your land",
  "Comparable transaction data from nearby plots",
  "Legal & compliance summary",
  "Expert review by our real estate team",
];

export default function FeasibilityReportPage() {
  usePageMeta({
    title: "AI Feasibility Report — Know What Your Land Is Worth | CashMyProperty.ai",
    description: "Get a 30-40 page AI feasibility report for your land in 48 hours. HBU scoring, top 3 monetization strategies, 5-year projections, zoning compliance and brand matching — ₹4,999.",
    canonicalUrl: "https://cashmyproperty.ai/feasibility-report",
  });
  const { status, errorMsg, submit } = useFormSubmit({
    formType: "feasibility-report",
  });
  const [city, setCity] = useState('');
  const [stateProvince, setStateProvince] = useState('');
  const hiddenLocation = city ? (stateProvince ? `${city}, ${stateProvince}` : city) : '';
  const { getContent } = usePageContent('feasibility-report');
  const { settings } = useSettings();
  const displayPhone = settings.general.primary_phone || '+91 90350 63455';
  const phoneRaw = settings.general.primary_phone_raw || '+919035063455';
  const contactEmail = settings.general.contact_email || 'support@cashmyproperty.ai';
  const heroBadge = stripHtml(getContent('feas-hero-badge')) || 'AI Report';
  const heroSub = stripHtml(getContent('feas-hero-sub')) || 'Know exactly what your land is worth — in 48 hours.';
  const includesHeading = stripHtml(getContent('feas-includes-heading')) || '30-40 Page AI Report';
  const priceText = stripHtml(getContent('feas-price')) || '₹4,999 — One-time fee per plot. Adjusted against success fee if you proceed.';
  const formHeading = stripHtml(getContent('feas-form-heading')) || 'Request Your AI Feasibility Report';
  const formSub = stripHtml(getContent('feas-form-sub')) || 'Fill in the details below. Our AI will analyze your land and deliver a comprehensive report within 48 hours.';

  return (
    <PageLayout>
      <PageHero
        badge={heroBadge}
        title="Get Your"
        highlight="AI Feasibility Report"
        subtitle={heroSub}
        image="https://readdy.ai/api/search-image?query=Professional%20AI%20real%20estate%20report%20document%20with%20charts%20and%20analytics%20glowing%20on%20dark%20background%2C%20holographic%20data%20visualization%2C%20futuristic%20technology%20concept%2C%20dark%20moody%20atmosphere%2C%20teal%20and%20amber%20neon%20accents%2C%20cinematic%20business%20photography%2C%20highly%20detailed&width=1800&height=800&seq=feasibility-hero-01&orientation=landscape"
      />

      {/* What's Included */}
      <section className="py-24 bg-background-50">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center max-w-5xl mx-auto">
            <div>
              <span className="inline-block text-sm text-primary-500 font-medium uppercase tracking-wider mb-3">
                What's Included
              </span>
              <h2 className="text-3xl md:text-4xl font-display font-bold text-foreground-950 mb-4">
                {includesHeading}
              </h2>
              <p className="text-foreground-600 leading-relaxed mb-6">
                The AI Feasibility Report is the most comprehensive land analysis available in India.
                Our HBU Engine, Valuation Predictor, and Brand Match Algorithm work together to deliver
                a report that tells you exactly what your land is worth and how to monetize it.
              </p>
              <ul className="space-y-3">
                {reportIncludes.map((item) => (
                  <li key={item} className="flex items-center gap-2 text-sm text-foreground-600">
                    <i className="ri-check-line text-primary-500 shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
              <div className="mt-8 p-4 rounded-xl bg-primary-500/10 border border-primary-700/30">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-background-50 border border-primary-500/30 shrink-0">
                    <i className="ri-time-line text-xl text-accent-500" />
                  </div>
                  <div>
                    <div className="text-sm font-semibold text-foreground-950">48-Hour Delivery</div>
                    <div className="text-xs text-foreground-500">
                      Report delivered within 48 hours of submission
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="space-y-6">
              <div className="p-6 rounded-2xl card-dark">
                <div className="flex items-center justify-between mb-4">
                  <span className="text-sm text-foreground-500 font-medium">Report Price</span>
                  <span className="text-3xl font-display font-bold text-foreground-950">{priceText.split('—')[0].trim()}</span>
                </div>
                <div className="text-sm text-foreground-500 mb-4">
                  One-time fee per plot. No hidden charges.
                </div>
                <div className="text-xs text-foreground-500 space-y-1">
                  <p>
                    <i className="ri-check-line text-primary-500 mr-1" />
                    Adjusted against success fee if you proceed
                  </p>
                  <p>
                    <i className="ri-check-line text-primary-500 mr-1" />
                    100% confidential & data secure
                  </p>
                </div>
              </div>
              <div className="p-6 rounded-2xl card-dark">
                <div className="text-sm font-semibold text-foreground-950 mb-2">
                  Need Help?
                </div>
                <div className="text-sm text-foreground-600 mb-4">
                  Call us at{" "}
                  <a href={`tel:${phoneRaw}`} className="text-primary-600 hover:text-primary-500">
                    {displayPhone}
                  </a>{" "}
                  or email{" "}
                  <a
                    href={`mailto:${contactEmail}`}
                    className="text-primary-600 hover:text-primary-500"
                  >
                    {contactEmail}
                  </a>
                </div>
                <Link
                  to="/contact"
                  className="inline-flex items-center gap-2 text-sm text-primary-600 font-semibold hover:text-primary-500 transition-colors"
                >
                  <i className="ri-message-3-line" />
                  Chat with a Strategist
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Form */}
      <section className="py-24 bg-background-100">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="max-w-2xl mx-auto">
            <div className="text-center mb-10">
              <h2 className="text-3xl font-display font-bold text-foreground-950 mb-3">
                {formHeading}
              </h2>
              <p className="text-foreground-600">
                {formSub}
              </p>
            </div>

            <form onSubmit={submit} className="p-8 rounded-2xl card-dark space-y-5">
              {/* Honeypot */}
              <input
                type="text"
                name="website_alt"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
                className="absolute opacity-0 w-0 h-0 pointer-events-none"
              />

              <div className="grid sm:grid-cols-2 gap-4">
                <input
                  name="name"
                  placeholder="Full Name *"
                  required
                  className="w-full px-4 py-3 rounded-xl bg-background-300/50 border border-background-400/30 text-foreground-950 placeholder:text-foreground-500 text-sm focus:outline-none focus:ring-2 focus:ring-primary-700/50"
                />
                <input
                  name="email"
                  type="email"
                  placeholder="Email Address *"
                  required
                  className="w-full px-4 py-3 rounded-xl bg-background-300/50 border border-background-400/30 text-foreground-950 placeholder:text-foreground-500 text-sm focus:outline-none focus:ring-2 focus:ring-primary-700/50"
                />
              </div>
              <input
                name="phone"
                type="tel"
                placeholder="Phone Number *"
                required
                className="w-full px-4 py-3 rounded-xl bg-background-300/50 border border-background-400/30 text-foreground-950 placeholder:text-foreground-500 text-sm focus:outline-none focus:ring-2 focus:ring-primary-700/50"
              />
              <CityAutocomplete
                value={city}
                onChange={setCity}
                onStateChange={setStateProvince}
                state={stateProvince || undefined}
                name="city"
                placeholder="City / Area *"
              />
              <select
                name="state_province"
                value={stateProvince}
                onChange={(e) => setStateProvince(e.target.value)}
                className="w-full px-4 py-3 rounded-xl bg-background-300/50 border border-background-400/30 text-foreground-950 text-sm focus:outline-none focus:ring-2 focus:ring-primary-700/50 cursor-pointer"
              >
                <option value="">Select State / UT</option>
                {INDIAN_STATES.map((state) => (
                  <option key={state} value={state}>
                    {state}
                  </option>
                ))}
              </select>
              <input type="hidden" name="location" value={hiddenLocation} />
              <div className="grid sm:grid-cols-2 gap-4">
                <select
                  name="landSize"
                  className="w-full px-4 py-3 rounded-xl bg-background-300/50 border border-background-400/30 text-foreground-950 text-sm focus:outline-none focus:ring-2 focus:ring-primary-700/50"
                >
                  <option value="">Land Size (Approx)</option>
                  <option value="small">Less than 5,000 sq.ft</option>
                  <option value="medium">5,000 – 10,000 sq.ft</option>
                  <option value="large">10,000 sq.ft – 1 Acre</option>
                  <option value="xlarge">1 Acre+</option>
                </select>
                <select
                  name="interest"
                  className="w-full px-4 py-3 rounded-xl bg-background-300/50 border border-background-400/30 text-foreground-950 text-sm focus:outline-none focus:ring-2 focus:ring-primary-700/50"
                >
                  <option value="">I'm interested in...</option>
                  <option value="jda">Joint Development (JDA)</option>
                  <option value="lease">Long-term Lease</option>
                  <option value="retail">Retail / Franchise</option>
                  <option value="equity">Land-as-Equity</option>
                  <option value="unsure">Not sure yet</option>
                </select>
              </div>
              <textarea
                name="message"
                placeholder="Tell us about your land (optional) — location details, current use, any specific goals..."
                rows={4}
                maxLength={500}
                className="w-full px-4 py-3 rounded-xl bg-background-300/50 border border-background-400/30 text-foreground-950 placeholder:text-foreground-500 text-sm focus:outline-none focus:ring-2 focus:ring-primary-700/50 resize-none"
              />

              {/* Hidden source */}
              <input type="hidden" name="source" value="Feasibility Report Page" />

              {status === "success" && (
                <div className="p-4 rounded-xl bg-primary-500/10 border border-primary-700/30 text-primary-600 text-sm text-center">
                  <i className="ri-checkbox-circle-line mr-2" />
                  Thank you! We&apos;ll analyze your land and deliver the report within 48 hours.
                </div>
              )}
              {status === "error" && (
                <div className="p-4 rounded-xl bg-red-500/10 border border-red-800/30 text-red-400 text-sm text-center">
                  <i className="ri-error-warning-line mr-2" />
                  {errorMsg || "Something went wrong. Please try again or call us directly."}
                </div>
              )}

              <button
                type="submit"
                disabled={status === "loading"}
                className="w-full py-3.5 rounded-full bg-primary-500 text-background-50 font-semibold hover:bg-primary-600 transition-colors disabled:opacity-60 cursor-pointer whitespace-nowrap"
              >
                {status === "loading" ? "Submitting..." : "Request Report — ₹4,999"}
              </button>
              <p className="text-xs text-foreground-500 text-center">
                No spam. 100% confidential. We never share your data.
              </p>
            </form>
          </div>
        </div>
      </section>
    </PageLayout>
  );
}